import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  ListItemText,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { PermissionKey } from '@c4ytechnology/authz';
import { AdminListPageHeader, adminDataTableSx, dataTableCardSx } from '../components/admin/adminListPageUi';
import { IamErrorAlert } from '../components/admin/iam/IamErrorAlert';
import {
  StaffUserPicker,
  StaffUsersMultiPicker,
  type StaffUserOption,
} from '../components/admin/iam/StaffUserPicker';
import { adminAccessReviewsApi, adminIamApi, type AdminAccessReviewCreate } from '../api/adminConsole';
import { getAdminConsoleToken } from '../lib/adminConsoleToken';
import { hasAdminPermission } from '../lib/adminPermissions';
import { ADMIN_IAM_ROLES_QUERY_KEY, sortRoles } from '../lib/adminRolesView';
import { isReviewOverdue, reviewProgressLabel } from '../lib/accessReviews';
import { expiryDateInputToIso, formatIamTimestamp, isoToExpiryDateInput } from '../lib/iamAssignmentStatus';
import { useAdminConsoleMe } from '../hooks/useAdminConsoleMe';

export const ACCESS_REVIEWS_QUERY_KEY = ['adminAccessReviews'] as const;

function CreateReviewDialog({ open, onClose, onCreated }: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const me = useAdminConsoleMe().data;
  const canReadRoles = hasAdminPermission(me, PermissionKey.IAM_ROLES_READ);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [due, setDue] = useState('');
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [minRisk, setMinRisk] = useState<'' | 'sensitive' | 'critical'>('');
  const [users, setUsers] = useState<StaffUserOption[]>([]);
  const [reviewer, setReviewer] = useState<StaffUserOption | null>(null);
  const [autoRevoke, setAutoRevoke] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName('');
    setDescription('');
    setDue(isoToExpiryDateInput(new Date(Date.now() + 14 * 24 * 3600 * 1000).toISOString()));
    setRoleIds([]);
    setMinRisk('');
    setUsers([]);
    setReviewer(null);
    setAutoRevoke(false);
  }, [open]);

  const rolesQ = useQuery({
    queryKey: ADMIN_IAM_ROLES_QUERY_KEY,
    queryFn: () => adminIamApi.roles(),
    enabled: open && canReadRoles,
    retry: false,
  });
  const roles = useMemo(() => sortRoles(rolesQ.data?.items ?? []), [rolesQ.data]);

  const dueIso = expiryDateInputToIso(due);
  const create = useMutation({
    mutationFn: () => {
      const body: AdminAccessReviewCreate = {
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        dueAt: dueIso!,
        scope: {
          ...(roleIds.length ? { roleIds } : {}),
          ...(minRisk ? { minRisk } : {}),
          ...(users.length ? { userIds: users.map((u) => u.id) } : {}),
        },
        ...(reviewer ? { reviewerUserId: reviewer.id } : {}),
        autoRevoke,
      };
      return adminAccessReviewsApi.create(body);
    },
    onSuccess: (review) => onCreated(review.id),
  });

  useEffect(() => {
    if (open) create.reset();
    // Reset only when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const scopeEmpty = roleIds.length === 0 && !minRisk && users.length === 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle>New access review</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2.5 }}>
        <IamErrorAlert error={create.error} />
        <TextField label="Name" size="small" required value={name} onChange={(e) => setName(e.target.value)} />
        <TextField
          label="Description (optional)"
          size="small"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          minRows={2}
        />
        <TextField
          label="Due date"
          type="date"
          size="small"
          required
          value={due}
          onChange={(e) => setDue(e.target.value)}
          error={!!due && !dueIso}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: -1 }}>
          Scope
        </Typography>
        <Typography variant="caption" color="text.secondary">
          One item is created per active role assignment that matches. Leave everything blank to review all
          assignments.
        </Typography>
        {canReadRoles && (
          <TextField
            select
            size="small"
            label="Roles"
            value={roleIds}
            onChange={(e) => {
              const v = e.target.value as unknown;
              setRoleIds(Array.isArray(v) ? v : String(v).split(',').filter(Boolean));
            }}
            slotProps={{
              select: {
                multiple: true,
                renderValue: (v) =>
                  (v as string[]).map((id) => roles.find((r) => r.id === id)?.name ?? id).join(', ') || 'Any role',
              },
              inputLabel: { shrink: true },
            }}
          >
            {roles.map((r) => (
              <MenuItem key={r.id} value={r.id} dense>
                <Checkbox size="small" checked={roleIds.includes(r.id)} />
                <ListItemText primary={r.name} />
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          select
          size="small"
          label="Minimum risk"
          value={minRisk}
          onChange={(e) => setMinRisk(e.target.value as '' | 'sensitive' | 'critical')}
          helperText="Only roles holding at least one permission of this risk."
        >
          <MenuItem value="">Any</MenuItem>
          <MenuItem value="sensitive">Sensitive or critical</MenuItem>
          <MenuItem value="critical">Critical</MenuItem>
        </TextField>
        <StaffUsersMultiPicker label="Users (optional)" value={users} onChange={setUsers} />
        <StaffUserPicker
          label="Reviewer"
          value={reviewer}
          onChange={setReviewer}
          helperText="Defaults to you. Nobody reviews their own assignment; those items go to you instead."
        />
        <FormControlLabel
          control={<Switch checked={autoRevoke} onChange={(_, v) => setAutoRevoke(v)} />}
          label="Revoke undecided items when the review is closed"
        />
        {autoRevoke && (
          <Alert severity="warning">Anything not explicitly kept by the close will lose access.</Alert>
        )}
        {scopeEmpty && <Alert severity="info">No scope set: every active assignment will be reviewed.</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim() || !dueIso || create.isPending}
          onClick={() => create.mutate()}
        >
          Create review
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Access review campaigns (RBAC Phase 2 item 8). */
export function AdminAccessReviewsPage() {
  const theme = useTheme();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const hasToken = typeof window !== 'undefined' && !!getAdminConsoleToken();
  const me = useAdminConsoleMe().data;
  const canManage = hasAdminPermission(me, PermissionKey.IAM_REVIEWS_MANAGE);
  const [createOpen, setCreateOpen] = useState(false);

  const listQ = useQuery({
    queryKey: ACCESS_REVIEWS_QUERY_KEY,
    queryFn: () => adminAccessReviewsApi.list(),
    enabled: hasToken,
  });

  const headerEnd = useMemo(
    () =>
      canManage ? (
        <Button variant="contained" size="small" onClick={() => setCreateOpen(true)}>
          New review
        </Button>
      ) : null,
    [canManage],
  );

  if (!hasToken) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">Sign in to view access reviews.</Alert>
      </Box>
    );
  }

  const items = listQ.data?.items ?? [];

  return (
    <Box>
      <AdminListPageHeader
        title="Access reviews"
        subtitle="Periodic campaigns where reviewers confirm or revoke each role assignment."
        endAdornment={headerEnd}
      />
      <IamErrorAlert error={listQ.error} sx={{ mb: 2 }} />
      <Card variant="outlined" sx={dataTableCardSx(theme)}>
        {listQ.isPending ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small" sx={adminDataTableSx(theme)}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Due</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Auto-revoke</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((r) => (
                <TableRow
                  key={r.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => void navigate(`/admin/access/reviews/${encodeURIComponent(r.id)}`)}
                >
                  <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                  <TableCell>
                    <Stack direction="row" gap={0.5}>
                      <Chip size="small" variant="outlined" color={r.status === 'open' ? 'primary' : 'default'} label={r.status === 'open' ? 'Open' : 'Closed'} />
                      {isReviewOverdue(r) && <Chip size="small" color="error" variant="outlined" label="Overdue" />}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatIamTimestamp(r.dueAt)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{reviewProgressLabel(r.counts)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {r.counts.keep} keep · {r.counts.revoke} revoke · {r.counts.undecided} undecided
                    </Typography>
                  </TableCell>
                  <TableCell>{r.autoRevoke ? 'On' : 'Off'}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{r.createdBy ?? '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatIamTimestamp(r.createdAt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary">
                      No access reviews yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Card>

      {canManage && (
        <CreateReviewDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={(id) => {
            setCreateOpen(false);
            void qc.invalidateQueries({ queryKey: ACCESS_REVIEWS_QUERY_KEY });
            void navigate(`/admin/access/reviews/${encodeURIComponent(id)}`);
          }}
        />
      )}
    </Box>
  );
}
